import type { ReviewWithReplyDto } from '@up2stay/up2promo-api-client';
import { Button, Card, cn, Skeleton } from '@up2stay/up2promo-ui';
import { ChevronLeft, ChevronRight, MessageSquare, X } from 'lucide-react';

import { ListEmptyState } from '@/core/components/layout';
import { ReputationSyncPlaceholder } from '@/core/components/reputation/reputation-sync-placeholder';
import { REPUTATION_SECTION_LABEL_CLASS } from '@/core/components/reputation/reputation-surface';
import { ReviewUrgentCard } from '@/core/components/reputation/reviews/review-urgent-card';
import { ReviewListItem } from '@/core/components/reputation/reviews/review-list-item';
import {
  isUrgentReview,
  type ReviewsInbox,
} from '@/core/components/reputation/reviews/use-reviews-inbox';
import { REVIEWS_PAGE_SIZE } from '@/core/components/reputation/reviews/reviews-list-query';

function GroupLabel({
  children,
  urgent = false,
}: {
  children: string;
  urgent?: boolean;
}) {
  return (
    <div
      className={cn(
        REPUTATION_SECTION_LABEL_CLASS,
        'px-4 pb-1.5 pt-3.5',
        urgent && 'text-destructive',
      )}
    >
      {children}
    </div>
  );
}

/** Scrollable review list: urgent items first on the open tab, then the rest. */
export function ReviewsListPane({
  inbox,
  onSelect,
  cards = false,
  className,
}: {
  inbox: ReviewsInbox;
  onSelect: (review: ReviewWithReplyDto) => void;
  /** Mobile: urgent reviews as expanded cards, the rest in their own card. */
  cards?: boolean;
  className?: string;
}) {
  const { reviewsDict, reviews, filters, activeReview, locale } = inbox;
  const sentimentLabels = reviewsDict.sentiment;
  const grouped = filters.tab === 'open';
  const urgent = grouped ? reviews.filter(isUrgentReview) : [];
  const rest = grouped ? reviews.filter((r) => !isUrgentReview(r)) : reviews;

  const renderItem = (review: ReviewWithReplyDto, isUrgent: boolean) => (
    <ReviewListItem
      key={review.id}
      review={review}
      locale={locale}
      sentimentLabels={sentimentLabels}
      noTextLabel={reviewsDict.detail.ratingOnly}
      active={activeReview?.id === review.id}
      urgent={isUrgent}
      onSelect={() => onSelect(review)}
    />
  );

  if (!inbox.isSyncCompleteForScope && !inbox.reviewsLoading) {
    return <ReputationSyncPlaceholder kind="inbox" />;
  }
  if (inbox.reviewsLoading) {
    return (
      <div className={cn('space-y-px', className)}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-[72px] w-full rounded-none" />
        ))}
      </div>
    );
  }
  if (reviews.length === 0) {
    return (
      <ListEmptyState
        className="border-0 shadow-none"
        icon={<MessageSquare className="h-5 w-5" />}
        title={
          inbox.hasActiveFilters
            ? reviewsDict.filters.empty
            : filters.tab === 'open' &&
                inbox.stats.scoped &&
                inbox.stats.total > 0
              ? reviewsDict.list.emptyOpen
              : reviewsDict.list.emptyNoReviews
        }
        action={
          inbox.hasActiveFilters ? (
            <Button variant="outline" size="sm" onClick={inbox.clearFilters}>
              <X className="mr-1 h-3.5 w-3.5" />
              {reviewsDict.filters.clear}
            </Button>
          ) : undefined
        }
      />
    );
  }

  const from = (inbox.page - 1) * REVIEWS_PAGE_SIZE + 1;
  const to = Math.min(inbox.page * REVIEWS_PAGE_SIZE, inbox.total);

  return (
    <div className={cn('flex min-h-0 flex-col', className)}>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {cards ? (
          <div className="flex flex-col gap-3">
            {grouped
              ? urgent.map((review) => (
                  <ReviewUrgentCard
                    key={review.id}
                    inbox={inbox}
                    review={review}
                    onOpen={() => onSelect(review)}
                  />
                ))
              : null}
            {rest.length > 0 ? (
              <div>
                {grouped ? (
                  <GroupLabel>{`${reviewsDict.groups.fresh} · ${rest.length}`}</GroupLabel>
                ) : null}
                <Card className="overflow-hidden">
                  {rest.map((review) => renderItem(review, false))}
                </Card>
              </div>
            ) : null}
          </div>
        ) : (
          <>
            {grouped && urgent.length > 0 ? (
              <>
                <GroupLabel urgent>
                  {`${reviewsDict.groups.urgent} · ${urgent.length}`}
                </GroupLabel>
                {urgent.map((review) => renderItem(review, true))}
              </>
            ) : null}
            {grouped && rest.length > 0 ? (
              <GroupLabel>{`${reviewsDict.groups.fresh} · ${rest.length}`}</GroupLabel>
            ) : null}
            {rest.map((review) => renderItem(review, false))}
          </>
        )}
      </div>

      {inbox.totalPages > 1 ? (
        <div className="text-muted-foreground flex shrink-0 items-center justify-between gap-2 border-t px-3 py-2 text-xs">
          <span className="tabular-nums">
            {reviewsDict.pagination.showing
              .replace('{from}', String(from))
              .replace('{to}', String(to))
              .replace('{total}', String(inbox.total))}
          </span>
          <span className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label={reviewsDict.pagination.previous}
              disabled={inbox.page <= 1}
              onClick={() => inbox.setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label={reviewsDict.pagination.next}
              disabled={inbox.page >= inbox.totalPages}
              onClick={() =>
                inbox.setPage((p) => Math.min(inbox.totalPages, p + 1))
              }
            >
              <ChevronRight className="size-4" />
            </Button>
          </span>
        </div>
      ) : null}
    </div>
  );
}
